import * as THREE from 'three';
import { countGeometryStats } from '../core/math';
import { createNacreMaterial, createStoneMaterial } from '../shaders/materials';
import type { SpecimenInstance, TemporalUniforms } from '../core/types';

export interface ShellOptions {
  turns?: number;
  expansion?: number;
  helicity?: number;
  ribFrequency?: number;
  ribAmplitude?: number;
  /** Target bounding radius in world units after normalization */
  scale?: number;
  thetaSegments?: number;
  phiSegments?: number;
}

/**
 * Generates a mathematical coiled seashell BufferGeometry using Raup's (1966)
 * helicospiral surface equations, normalized so `scale` directly controls its
 * exact world-space radius.
 */
export function createCoiledShellGeometry(opts: ShellOptions = {}): THREE.BufferGeometry {
  const turns = opts.turns ?? 3.0;
  const b = opts.expansion ?? 0.165;
  const helicity = opts.helicity ?? 0.0;
  const ribFreq = opts.ribFrequency ?? 24;
  const ribAmp = opts.ribAmplitude ?? 0.085;
  const targetRadius = opts.scale ?? 1.0;
  const uSteps = opts.thetaSegments ?? 120;
  const vSteps = opts.phiSegments ?? 32;

  const thetaMax = turns * Math.PI * 2;
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= uSteps; i++) {
    const u = i / uSteps;
    const theta = u * thetaMax;
    const growth = Math.exp(b * theta);

    const rCenter = 0.14 * growth;
    const zOffset = helicity * 0.16 * (growth - 1.0);
    const varix = Math.pow(0.5 + 0.5 * Math.cos(theta * ribFreq), 3.0) * ribAmp;

    for (let j = 0; j <= vSteps; j++) {
      const v = j / vSteps;
      const phi = v * Math.PI * 2;

      const spiralStriation = 0.03 * Math.sin(phi * 12.0);
      const aRadius = 0.095 * growth * (1.0 + varix + spiralStriation);
      const bRadius = 0.118 * growth * (1.0 + varix * 0.85 + spiralStriation);

      const localR = rCenter + aRadius * Math.cos(phi);
      const x = localR * Math.cos(theta);
      const z = localR * Math.sin(theta);
      const y = -zOffset + bRadius * Math.sin(phi);

      positions.push(x, y, z);
      normals.push(0, 1, 0);
      uvs.push(u, v);
    }
  }

  const stride = vSteps + 1;
  for (let i = 0; i < uSteps; i++) {
    for (let j = 0; j < vSteps; j++) {
      const a = i * stride + j;
      const bIdx = (i + 1) * stride + j;
      const c = (i + 1) * stride + (j + 1);
      const d = i * stride + (j + 1);

      indices.push(a, bIdx, d);
      indices.push(bIdx, c, d);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setIndex(indices);
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geom.center();
  geom.computeBoundingSphere();

  const currentRadius = geom.boundingSphere?.radius || 1.0;
  const normFactor = targetRadius / currentRadius;
  geom.scale(normFactor, normFactor, normFactor);
  geom.computeVertexNormals();
  geom.computeBoundingSphere();

  return geom;
}

/**
 * Creates a planispiral chambered Nautilus shell geometry with exact bounding radius `scale`.
 */
export function createNautilusShellGeometry(scale = 0.35, detail = 1.0): THREE.BufferGeometry {
  return createCoiledShellGeometry({
    turns: 2.75,
    expansion: 0.175,
    helicity: 0.04,
    ribFrequency: 24,
    ribAmplitude: 0.065,
    scale,
    thetaSegments: Math.round(110 * detail),
    phiSegments: Math.round(30 * detail),
  });
}

/**
 * Creates a high-spired "Precious Staircase" Wentletrap / Turritella shell geometry
 * with exact bounding radius `scale`.
 */
export function createTurritellaShellGeometry(scale = 0.38, detail = 1.0): THREE.BufferGeometry {
  return createCoiledShellGeometry({
    turns: 4.8,
    expansion: 0.088,
    helicity: 2.5,
    ribFrequency: 13,
    ribAmplitude: 0.16,
    scale,
    thetaSegments: Math.round(130 * detail),
    phiSegments: Math.round(26 * detail),
  });
}

/**
 * Builds the isolated Seashell Trio Specimen for the Inspector turntable.
 */
export function buildSeashellSpecimen(uniforms: TemporalUniforms): SpecimenInstance {
  const group = new THREE.Group();

  const nautilusMat = createNacreMaterial(uniforms, 0.15);
  const wentletrapMat = createNacreMaterial(uniforms, 1.45);
  const pearlStandMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x4b3842,
    baseMemoryHex: 0x24304a,
    roughness: 0.35,
    metalness: 0.55,
  });

  // 1. Central Chambered Nautilus (normalized radius = 1.05m)
  const nautilusGeo = createNautilusShellGeometry(1.05, 1.35);
  const nautilusMesh = new THREE.Mesh(nautilusGeo, nautilusMat);
  nautilusMesh.position.set(-1.15, 1.85, 0);
  nautilusMesh.rotation.z = Math.PI * 0.1;
  nautilusMesh.castShadow = true;
  group.add(nautilusMesh);

  // 2. Sculptural Wentletrap ("Precious Staircase" shell, normalized radius = 1.15m)
  const turritellaGeo = createTurritellaShellGeometry(1.15, 1.35);
  const turritellaMesh = new THREE.Mesh(turritellaGeo, wentletrapMat);
  turritellaMesh.position.set(1.15, 1.95, 0.15);
  turritellaMesh.rotation.x = -0.12;
  turritellaMesh.castShadow = true;
  group.add(turritellaMesh);

  // 3. Golden Logarithmic Spiral Septa Overlay
  const curvePoints: THREE.Vector3[] = [];
  for (let i = 0; i <= 140; i++) {
    const theta = (i / 140) * Math.PI * 4.8;
    const r = 0.045 * Math.exp(0.175 * theta);
    curvePoints.push(new THREE.Vector3(r * Math.cos(theta), r * Math.sin(theta), 0));
  }
  const spiralCurve = new THREE.CatmullRomCurve3(curvePoints);
  const wireGeo = new THREE.TubeGeometry(spiralCurve, 120, 0.014, 10, false);
  const wireMat = new THREE.MeshStandardMaterial({
    color: 0xf4c46e,
    emissive: 0xc87d28,
    emissiveIntensity: 0.35,
    metalness: 0.85,
    roughness: 0.2,
  });
  const wireMesh = new THREE.Mesh(wireGeo, wireMat);
  wireMesh.position.set(-1.15, 1.85, 0.42);
  group.add(wireMesh);

  // Brass display pedestals
  for (const xPos of [-1.15, 1.15]) {
    const stemGeo = new THREE.CylinderGeometry(0.045, 0.14, 1.1, 16);
    const stem = new THREE.Mesh(stemGeo, pearlStandMat);
    stem.position.set(xPos, 0.55, xPos > 0 ? 0.15 : 0);
    group.add(stem);
  }

  const stats = countGeometryStats(group);

  return {
    group,
    update: (elapsed) => {
      nautilusMesh.rotation.y = Math.sin(elapsed * 0.5) * 0.25;
      wireMesh.rotation.y = nautilusMesh.rotation.y;
      turritellaMesh.rotation.y = elapsed * 0.35;
    },
    dispose: () => {
      nautilusGeo.dispose();
      turritellaGeo.dispose();
      wireGeo.dispose();
    },
    meta: {
      vertices: stats.vertices,
      triangles: stats.triangles,
      formula: 'r(θ) = r₀·e^(bθ),  z(θ) = h·(e^(bθ) − 1) + A·cos³(kθ)',
      parameters: {
        'Nautilus Expansion (b)': 0.175,
        'Wentletrap Helicity (h)': 2.5,
        'Varix Rib Frequency (k)': 13,
        'Iridescence Model': 'Subtle Nacre Pearl Interference',
      },
    },
  };
}
